// Conferência do Faturamento — uma linha por UC × mês, partindo da planilha de rateio.
// Projeto 12 "Controle de faturas e faturamento", tarefa 5836.
//
// A regra toda está em src/lib/conferenciaFaturamento.ts (com testes). Esta tela só
// busca, filtra e desenha. Acesso: adm, gerente e analista (rota e menu).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Download,
  FileQuestion,
  HelpCircle,
  RefreshCw,
  Search,
  XCircle,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import InfoTip from '../../src/components/InfoTip';
import {
  DIAS_ALERTA_LIMITE,
  ROTULO_DETALHE,
  ROTULO_SITUACAO,
  mesDeOrdem,
  ordemMes,
  resumirConferencia,
  type DetalheConferencia,
  type EstadoPrazo,
  type LinhaConferencia,
  type SituacaoFaturamento,
} from '../../src/lib/conferenciaFaturamento';
import { carregarConferencia, hojeIsoLocal as hojeIso, mesAtualFaturas, type CargaConferencia as Carga } from '../../src/lib/conferenciaFaturamentoDb';

const LINHAS_POR_PAGINA = 100;

const dataBr = (v: string | null | undefined) => {
  if (!v) return '—';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s) && s.length === 10) return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

const ESTILO_SITUACAO: Record<SituacaoFaturamento, { classe: string; Icone: typeof CheckCircle2 }> = {
  faturado: { classe: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', Icone: CheckCircle2 },
  nao_faturado: { classe: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', Icone: XCircle },
  nao_verificavel: { classe: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200', Icone: FileQuestion },
};

const textoPrazo = (l: LinhaConferencia): { texto: string; classe: string } => {
  const neutro = 'text-gray-500 dark:text-gray-400';
  const estados: Record<EstadoPrazo, () => { texto: string; classe: string }> = {
    no_prazo: () => ({ texto: 'No prazo', classe: 'text-emerald-700 dark:text-emerald-400' }),
    fora_do_prazo: () => ({ texto: `${l.diasPrazo} dia(s) após o limite`, classe: 'text-amber-700 dark:text-amber-400 font-semibold' }),
    apos_vencimento: () => ({ texto: 'Após o vencimento', classe: 'text-red-700 dark:text-red-400 font-semibold' }),
    aberto: () => ({ texto: `Faltam ${l.diasPrazo} dia(s)`, classe: neutro }),
    limite_proximo: () => ({ texto: l.diasPrazo === 0 ? 'Limite é hoje' : `Faltam ${l.diasPrazo} dia(s)`, classe: 'text-amber-700 dark:text-amber-400 font-semibold' }),
    limite_vencido: () => ({ texto: `Limite passou há ${Math.abs(l.diasPrazo ?? 0)} dia(s)`, classe: 'text-red-700 dark:text-red-400 font-semibold' }),
    sem_prazo: () => ({ texto: '—', classe: neutro }),
  };
  return estados[l.estadoPrazo]();
};

type FiltroRapido = '' | 'sem_justificativa' | 'em_apuracao' | 'fora_prazo_sem_justificativa' | 'limite' | 'nao_recebida' | 'fora_planilha';

const ESTILO_CAMPO =
  'rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-primary dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200';

interface CartaoProps {
  titulo: string;
  valor: number;
  sub?: string;
  tom?: 'ok' | 'alerta' | 'perigo' | 'neutro';
  /** Com filtro, o cartão vira botão que liga/desliga o filtro rápido. */
  filtro?: FiltroRapido;
  ativo?: boolean;
  onAlternar?: (f: FiltroRapido) => void;
  dica?: React.ReactNode;
}

const COR_TOM: Record<NonNullable<CartaoProps['tom']>, string> = {
  ok: 'text-emerald-700 dark:text-emerald-400',
  alerta: 'text-amber-700 dark:text-amber-400',
  perigo: 'text-red-700 dark:text-red-400',
  neutro: 'text-gray-900 dark:text-white',
};

/**
 * O cartão com filtro é clicável inteiro, mas o gatilho da dica também é um
 * botão — e botão dentro de botão é HTML inválido: o clique na dica subia para
 * o cartão e ligava o filtro sem querer, e o leitor de tela lia os dois juntos.
 * Aqui o cartão é uma div e quem recebe o clique é um botão sobreposto
 * (`absolute inset-0`), com a dica acima dele (`relative z-10`). Visualmente
 * idêntico, com dois alvos independentes para clique e teclado.
 */
const Cartao: React.FC<CartaoProps> = ({ titulo, valor, sub, tom = 'neutro', filtro, ativo = false, onAlternar, dica }) => {
  const clicavel = !!filtro && !!onAlternar;
  return (
    <div className={`relative rounded-2xl border bg-white p-4 text-left shadow-sm dark:bg-gray-800/60 ${ativo ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 dark:border-gray-700'} ${clicavel ? 'transition-colors hover:border-primary focus-within:border-primary' : ''}`}>
      {clicavel && (
        <button
          type="button"
          onClick={() => onAlternar!(filtro!)}
          aria-pressed={ativo}
          aria-label={`Filtrar por ${titulo}`}
          className="absolute inset-0 z-0 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        />
      )}
      <p className="pointer-events-none relative z-10 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {titulo}{dica && <span className="pointer-events-auto"><InfoTip label={titulo}>{dica}</InfoTip></span>}
      </p>
      <p className={`pointer-events-none relative z-10 mt-1 text-2xl font-bold tabular-nums ${COR_TOM[tom]}`}>{valor.toLocaleString('pt-BR')}</p>
      {sub && <p className="pointer-events-none relative z-10 mt-0.5 text-xs text-gray-500 dark:text-gray-400">{sub}</p>}
    </div>
  );
};

const ConferenciaFaturamentoView: React.FC = () => {
  const [mes, setMes] = useState(mesAtualFaturas());
  const [carga, setCarga] = useState<Carga | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState<SituacaoFaturamento | ''>('');
  const [detalhe, setDetalhe] = useState<DetalheConferencia | ''>('');
  const [statusRateio, setStatusRateio] = useState('');
  const [rapido, setRapido] = useState<FiltroRapido>('');
  const [pagina, setPagina] = useState(1);

  const carregar = useCallback(async (mesAlvo: string) => {
    setCarregando(true);
    setErro(null);
    try {
      setCarga(await carregarConferencia(mesAlvo));
    } catch (e: any) {
      console.error('Conferência do faturamento:', e);
      setErro(e?.message || 'Não foi possível carregar a conferência.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(mes); }, [mes, carregar]);
  useEffect(() => { setPagina(1); }, [busca, situacao, detalhe, statusRateio, rapido, mes]);

  const resumo = useMemo(() => (carga ? resumirConferencia(carga.resultado) : null), [carga]);

  const opcoesMes = useMemo(() => {
    const atual = ordemMes(mesAtualFaturas())!;
    return Array.from({ length: 13 }, (_, i) => mesDeOrdem(atual + 1 - i));
  }, []);

  const statusDisponiveis = useMemo(
    () => (resumo ? Object.keys(resumo.porStatusRateio).sort((a, b) => a.localeCompare(b, 'pt-BR')) : []),
    [resumo],
  );

  const linhasFiltradas = useMemo(() => {
    if (!carga) return [];
    const origem = rapido === 'fora_planilha' ? carga.resultado.foraDaPlanilha : carga.resultado.linhas;
    const termo = busca.trim().toLowerCase();
    const termoNum = termo.replace(/\D/g, '').replace(/^0+/, '');
    return origem.filter((l) => {
      if (situacao && l.situacao !== situacao) return false;
      if (detalhe && l.detalhe !== detalhe) return false;
      if (statusRateio && rapido !== 'fora_planilha' && l.statusRateio !== statusRateio) return false;
      if (rapido === 'sem_justificativa' && !(l.situacao === 'nao_faturado' && l.detalhe !== 'pulado_justificado' && l.detalhe !== 'pulado_em_apuracao')) return false;
      if (rapido === 'em_apuracao' && l.detalhe !== 'pulado_em_apuracao') return false;
      if (rapido === 'fora_prazo_sem_justificativa' && !((l.estadoPrazo === 'fora_do_prazo' || l.estadoPrazo === 'apos_vencimento') && !l.atrasoJustificado)) return false;
      if (rapido === 'limite' && l.estadoPrazo !== 'limite_proximo' && l.estadoPrazo !== 'limite_vencido') return false;
      if (rapido === 'nao_recebida' && l.detalhe !== 'fatura_nao_recebida') return false;
      if (termo) {
        const numeros = [l.chave, l.uc, l.outroNumeroUc].map((n) => (n ?? '').replace(/\D/g, '').replace(/^0+/, ''));
        const casaNumero = !!termoNum && numeros.some((n) => n.includes(termoNum));
        if (!casaNumero && !l.cliente.toLowerCase().includes(termo)) return false;
      }
      return true;
    });
  }, [carga, busca, situacao, detalhe, statusRateio, rapido]);

  const totalPaginas = Math.max(1, Math.ceil(linhasFiltradas.length / LINHAS_POR_PAGINA));
  const visiveis = linhasFiltradas.slice((pagina - 1) * LINHAS_POR_PAGINA, pagina * LINHAS_POR_PAGINA);

  const exportar = useCallback(() => {
    if (!carga) return;
    const cab = [
      'UC', 'Outro nº UC', 'Cliente', 'Status no rateio', 'Usinas', 'Marcação na planilha', 'Situação', 'Detalhe',
      'Fatura (id)', 'Chegada da fatura', 'Vencimento distribuidora', 'Limite de emissão', 'Prazo',
      'Emitido em', 'Emitido por', 'Reemitido', 'Motivo do atraso', 'Pulado em', 'Pulado por', 'Motivo (lista)',
      'Motivo (texto)', 'Motivo antigo (texto livre)', 'Chegada prevista', 'Fatura atrasada',
    ];
    const linhas = linhasFiltradas.map((l) => [
      l.uc, l.outroNumeroUc ?? '', l.cliente, l.statusRateio, l.usinas.join(', '), l.marcacoesRateio.join(', '),
      ROTULO_SITUACAO[l.situacao], ROTULO_DETALHE[l.detalhe], l.faturaId ?? '', dataBr(l.chegadaEm),
      dataBr(l.dataVencimento), dataBr(l.limiteEmissao), textoPrazo(l).texto, dataBr(l.emitidoEm), l.emitidoPor ?? '',
      l.reemitido ? 'Sim' : '', l.motivoAtraso ?? '', dataBr(l.puladoEm), l.puladoPor ?? '', l.motivoRotulo ?? '',
      l.motivoTexto ?? '', l.motivoLegado ? 'Sim' : '', dataBr(l.chegadaPrevista), l.chegadaAtrasada ? 'Sim' : '',
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cab, ...linhas]), 'Conferencia');
    XLSX.writeFile(wb, `Conferencia_Faturamento_${carga.mes.replace('/', '_')}_${hojeIso()}.xlsx`);
  }, [carga, linhasFiltradas]);

  const limparFiltros = () => {
    setBusca(''); setSituacao(''); setDetalhe(''); setStatusRateio(''); setRapido('');
  };

  if (!carga && carregando) return <LoadingSpinner label="Montando a conferência do faturamento..." />;

  if (!carga && erro) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          <p className="font-semibold">Não foi possível carregar a conferência.</p>
          <p className="mt-1 text-sm">{erro}</p>
          <button onClick={() => carregar(mes)} className="mt-3 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700">
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  const planilhaDeOutroMes = !!carga && !!carga.planilhaMes && carga.planilhaMes !== carga.planilhaEsperada;
  const alternarRapido = (f: FiltroRapido) => setRapido((atual) => (atual === f ? '' : f));

  return (
    <div className="space-y-5 p-4 pb-24 sm:p-6 sm:pb-24">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
            <ClipboardCheck size={22} className="text-primary" />
            Conferência do Faturamento
            <InfoTip label="Como a conferência é montada">
              <p className="mb-2">
                A base é a <strong>planilha de rateio do mês anterior</strong> (rateio de JUL vale para as faturas de AGO) — toda UC dela, com qualquer status, inclusive
                "Cancelada". Assim aparece também a UC cuja fatura nunca chegou, que não entra em fila nenhuma.
              </p>
              <p className="mb-2">
                <strong>Faturado</strong> = boleto emitido com registro no banco e não cancelado. Pular o cálculo, mesmo
                com motivo, continua sendo <strong>não faturado</strong>.
              </p>
              <p>
                O prazo é o mesmo da Janela de Faturamento: vencimento da distribuidora menos 7 dias corridos
                (ajustado para segunda-feira). Emissão medida na primeira vez que o boleto saiu.
              </p>
            </InfoTip>
          </h1>
          {carga && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {carga.planilhaMes
                ? <>Faturas de <strong>{carga.mes}</strong> × planilha de rateio de <strong>{carga.planilhaMes}</strong> (carregada em {dataBr(carga.planilhaCarregadaEm)})</>
                : `Nenhuma planilha de rateio carregada até ${carga.planilhaEsperada ?? 'o mês anterior'}`}
              {' · '}posição de {new Date().toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="conferencia-mes">Mês de referência das faturas</label>
          <select id="conferencia-mes" value={mes} onChange={(e) => setMes(e.target.value)} className={ESTILO_CAMPO}>
            {opcoesMes.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button
            onClick={() => carregar(mes)}
            disabled={carregando}
            aria-busy={carregando}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <RefreshCw size={15} className={carregando ? 'animate-spin' : ''} /> {carregando ? 'Atualizando...' : 'Atualizar'}
          </button>
          <button
            onClick={exportar}
            disabled={linhasFiltradas.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Download size={15} /> Exportar Excel
          </button>
        </div>
      </header>

      {erro && carga && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          Não foi possível atualizar: {erro}. Os números abaixo são da última carga.
        </div>
      )}

      {planilhaDeOutroMes && (
        <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <p>
            A planilha de rateio de <strong>{carga!.planilhaEsperada}</strong>, que vale para as faturas de {carga!.mes}, ainda não foi carregada. A base usa a de{' '}
            <strong>{carga!.planilhaMes}</strong>: UC que entrou no rateio depois dela aparece em "Fora da planilha", e UC
            que saiu continua na base.
          </p>
        </div>
      )}

      {carga && !carga.planilhaMes && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          Sem planilha de rateio até {carga.planilhaEsperada}: não há base para conferir. As faturas do mês aparecem em "Fora da planilha".
        </div>
      )}

      {resumo && (
        <>
          <section aria-label="Resumo" className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
            <Cartao titulo="Base" valor={resumo.base} sub="UCs da planilha" dica={
              <p>Faturado + não faturado + não verificável = base. Uma UC em duas usinas conta uma vez.</p>
            } />
            <Cartao titulo="Faturado" valor={resumo.faturado} tom="ok" sub={resumo.base ? `${Math.round((resumo.faturado / resumo.base) * 100)}% da base` : undefined} />
            <Cartao titulo="Não faturado" valor={resumo.naoFaturado} tom="perigo" sub={`${resumo.puladoJustificado} com motivo`} />
            <Cartao titulo="Não verificável" valor={resumo.naoVerificavel} sub={`${resumo.faturaNaoRecebida} sem fatura`} />
            <Cartao titulo="Sem justificativa" valor={resumo.naoFaturadoSemJustificativa} tom="perigo" filtro="sem_justificativa" ativo={rapido === 'sem_justificativa'} onAlternar={alternarRapido} dica={
              <p>Não faturado sem motivo: aguardando faturamento, boleto cancelado sem reemissão ou boleto sem registro no banco.</p>
            } />
            <Cartao titulo="Em apuração" valor={resumo.emApuracao} tom="alerta" filtro="em_apuracao" ativo={rapido === 'em_apuracao'} onAlternar={alternarRapido} dica={
              <p>Pulado como "sem novos créditos", mas a própria fatura mostra energia injetada ou saldo das usinas Vigor. Pode ser crédito de outra origem — precisa conferir.</p>
            } />
            <Cartao titulo={`Limite ≤ ${DIAS_ALERTA_LIMITE} dias`} valor={resumo.limiteProximo + resumo.limiteVencido} tom="alerta" filtro="limite" ativo={rapido === 'limite'} onAlternar={alternarRapido} sub={`${resumo.limiteVencido} já passaram`} />
            <Cartao titulo="Fora do prazo" valor={resumo.foraDoPrazo} tom="alerta" filtro="fora_prazo_sem_justificativa" ativo={rapido === 'fora_prazo_sem_justificativa'} onAlternar={alternarRapido} sub={`${resumo.foraDoPrazoSemJustificativa} sem motivo · ${resumo.aposVencimento} após venc.`} />
          </section>

          <div className="flex flex-wrap gap-2 text-xs">
            <button type="button" onClick={() => alternarRapido('nao_recebida')} aria-pressed={rapido === 'nao_recebida'}
              className={`rounded-full border px-3 py-1 ${rapido === 'nao_recebida' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-300'}`}>
              Fatura não recebida: {resumo.faturaNaoRecebida} ({resumo.faturaNaoRecebidaAtrasada} atrasadas)
            </button>
            <button type="button" onClick={() => alternarRapido('fora_planilha')} aria-pressed={rapido === 'fora_planilha'}
              className={`rounded-full border px-3 py-1 ${rapido === 'fora_planilha' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-300'}`}>
              Fora da planilha: {resumo.foraDaPlanilha} (não entram na base)
            </button>
            {Object.entries(resumo.porStatusRateio).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
              <span key={s} className="rounded-full bg-gray-100 px-3 py-1 text-gray-600 dark:bg-gray-800 dark:text-gray-300">{s}: {n}</span>
            ))}
          </div>
        </>
      )}

      <section className="flex flex-wrap items-center gap-2" aria-label="Filtros">
        <div className="relative min-w-[220px] flex-1">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar UC ou cliente"
            aria-label="Buscar UC ou cliente"
            className={`${ESTILO_CAMPO} w-full pl-9`}
          />
        </div>
        <select value={situacao} onChange={(e) => setSituacao(e.target.value as SituacaoFaturamento | '')} aria-label="Situação" className={ESTILO_CAMPO}>
          <option value="">Todas as situações</option>
          {(Object.keys(ROTULO_SITUACAO) as SituacaoFaturamento[]).map((s) => <option key={s} value={s}>{ROTULO_SITUACAO[s]}</option>)}
        </select>
        <select value={detalhe} onChange={(e) => setDetalhe(e.target.value as DetalheConferencia | '')} aria-label="Detalhe" className={ESTILO_CAMPO}>
          <option value="">Todos os detalhes</option>
          {(Object.keys(ROTULO_DETALHE) as DetalheConferencia[]).map((d) => <option key={d} value={d}>{ROTULO_DETALHE[d]}</option>)}
        </select>
        <select value={statusRateio} onChange={(e) => setStatusRateio(e.target.value)} aria-label="Status no rateio" className={ESTILO_CAMPO}>
          <option value="">Todos os status do rateio</option>
          {statusDisponiveis.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {(busca || situacao || detalhe || statusRateio || rapido) && (
          <button type="button" onClick={limparFiltros} className="text-sm font-medium text-primary hover:underline">Limpar filtros</button>
        )}
        <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">{linhasFiltradas.length.toLocaleString('pt-BR')} linha(s)</span>
      </section>

      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800/60">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2">UC / Cliente</th>
              <th className="px-3 py-2">Rateio</th>
              <th className="px-3 py-2">Situação</th>
              <th className="px-3 py-2">Fatura</th>
              <th className="px-3 py-2">Prazo</th>
              <th className="px-3 py-2">Motivo / Quem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {visiveis.map((l) => {
              const { Icone, classe } = ESTILO_SITUACAO[l.situacao];
              const prazo = textoPrazo(l);
              return (
                <tr key={`${l.chave}-${l.faturaId ?? 'x'}`} className="align-top hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-3 py-2">
                    <p className="font-mono text-xs text-gray-900 dark:text-white">{l.uc}</p>
                    {l.outroNumeroUc && <p className="font-mono text-[11px] text-gray-400">{l.outroNumeroUc}</p>}
                    <p className="max-w-[240px] truncate text-xs text-gray-600 dark:text-gray-300" title={l.cliente}>{l.cliente || '—'}</p>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <p className="font-medium text-gray-800 dark:text-gray-200">{l.statusRateio}</p>
                    {l.usinas.length > 0 && <p className="text-gray-500 dark:text-gray-400">{l.usinas.join(', ')}</p>}
                    {l.marcacoesRateio.length > 0 && <p className="text-gray-500 dark:text-gray-400">{l.marcacoesRateio.join(', ')}</p>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${classe}`}>
                      <Icone size={12} /> {ROTULO_SITUACAO[l.situacao]}
                    </span>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {ROTULO_DETALHE[l.detalhe]}{l.reemitido && ' · reemitido'}
                    </p>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                    {l.faturaId ? (
                      <>
                        <p>Chegou {dataBr(l.chegadaEm)}{l.quantidadeFaturas > 1 && ` · ${l.quantidadeFaturas} linhas`}</p>
                        <p>Vence {dataBr(l.dataVencimento)}</p>
                        <p className="text-gray-400">#{l.faturaId}</p>
                      </>
                    ) : l.chegadaPrevista ? (
                      <p className={l.chegadaAtrasada ? 'font-semibold text-red-700 dark:text-red-400' : ''}>
                        Prevista {dataBr(l.chegadaPrevista)}{l.chegadaAtrasada && ' · atrasada'}
                      </p>
                    ) : <p className="text-gray-400">Sem previsão</p>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <p className={prazo.classe}>{prazo.texto}</p>
                    {l.limiteEmissao && <p className="text-gray-400">Limite {dataBr(l.limiteEmissao)}</p>}
                    {l.emitidoEm && <p className="text-gray-500 dark:text-gray-400">Emitido {dataBr(l.emitidoEm)}</p>}
                  </td>
                  <td className="max-w-[280px] px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                    {l.motivoRotulo && (
                      <p>
                        <span className="font-medium">{l.motivoRotulo}</span>
                        {l.motivoLegado && (
                          <span className="ml-1 inline-flex items-center text-gray-400" title={`Texto original: ${l.motivoTexto ?? ''}`}>
                            <HelpCircle size={11} className="mr-0.5" />texto antigo
                          </span>
                        )}
                      </p>
                    )}
                    {l.motivoTexto && !l.motivoLegado && l.motivoTexto !== l.motivoRotulo && <p className="truncate" title={l.motivoTexto}>{l.motivoTexto}</p>}
                    {l.motivoAtraso && <p><span className="font-medium">Atraso:</span> {l.motivoAtraso}</p>}
                    {(l.estadoPrazo === 'fora_do_prazo' || l.estadoPrazo === 'apos_vencimento') && !l.atrasoJustificado && (
                      <p className="font-semibold text-amber-700 dark:text-amber-400">Atraso sem motivo</p>
                    )}
                    {(l.puladoPor || l.emitidoPor) && <p className="text-gray-400">{l.puladoPor ?? l.emitidoPor}</p>}
                  </td>
                </tr>
              );
            })}
            {visiveis.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  <CalendarClock size={22} className="mx-auto mb-2 text-gray-300" />
                  Nenhuma UC com esses filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPaginas > 1 && (
        <nav className="flex items-center justify-end gap-2 text-sm" aria-label="Paginação">
          <button type="button" disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)} className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-40 dark:border-gray-700 dark:text-gray-200">Anterior</button>
          <span className="text-gray-500 dark:text-gray-400">Página {pagina} de {totalPaginas}</span>
          <button type="button" disabled={pagina === totalPaginas} onClick={() => setPagina((p) => p + 1)} className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-40 dark:border-gray-700 dark:text-gray-200">Próxima</button>
        </nav>
      )}
    </div>
  );
};

export default ConferenciaFaturamentoView;
